<template>
  <Teleport to="body">
    <Transition name="slide-down">
      <div v-if="hasErrors" class="global-error-container">
        <div
          v-for="error in displayedErrors"
          :key="error.id"
          :class="['global-error-banner', `error-type-${error.type}`]"
        >
          <div class="error-icon">
            <svg v-if="error.type === 'error'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <svg v-else-if="error.type === 'warning'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <svg v-else fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div class="error-content">
            <h4>{{ error.title }}</h4>
            <p>{{ error.message }}</p>
          </div>
          <button
            v-if="error.dismissible"
            @click="dismissError(error.id)"
            class="btn-dismiss"
            aria-label="Dismiss error"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useAppStore } from '@/stores/app'

const appStore = useAppStore()

const hasErrors = computed(() => appStore.hasGlobalErrors)

// Only show the last 3 errors to avoid overwhelming the user
const displayedErrors = computed(() => {
  const errors = appStore.globalErrors
  return errors.slice(-3)
})

const dismissError = (id: string) => {
  appStore.dismissGlobalError(id)
}
</script>

<style scoped>
.global-error-container {
  position: fixed;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 600px;
  width: calc(100% - 2rem);
  pointer-events: none;
}

.global-error-banner {
  background: white;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 4px 10px rgba(0, 0, 0, 0.1);
  pointer-events: auto;
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
}

/* Error type styles */
.error-type-error {
  border-left: 4px solid #ef4444;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
}

.error-type-warning {
  border-left: 4px solid #f59e0b;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
}

.error-type-info {
  border-left: 4px solid #3b82f6;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
}

.error-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-type-error .error-icon svg {
  color: #ef4444;
}

.error-type-warning .error-icon svg {
  color: #f59e0b;
}

.error-type-info .error-icon svg {
  color: #3b82f6;
}

.error-content {
  flex: 1;
  min-width: 0;
}

.error-content h4 {
  margin: 0 0 0.25rem 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
}

.error-content p {
  margin: 0;
  font-size: 0.875rem;
  color: #4b5563;
  line-height: 1.4;
}

.btn-dismiss {
  flex-shrink: 0;
  background: transparent;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
  color: #9ca3af;
  border-radius: 4px;
  transition: all 0.2s;
}

.btn-dismiss:hover {
  color: #4b5563;
  background: rgba(0, 0, 0, 0.05);
}

.btn-dismiss svg {
  width: 20px;
  height: 20px;
}

/* Transition animations */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(-20px);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-20px);
}

/* Mobile styles */
@media (max-width: 640px) {
  .global-error-container {
    top: 0.5rem;
    width: calc(100% - 1rem);
  }

  .global-error-banner {
    padding: 0.875rem 1rem;
    gap: 0.75rem;
    border-radius: 10px;
  }

  .error-icon {
    width: 20px;
    height: 20px;
  }

  .error-content h4 {
    font-size: 0.9rem;
  }

  .error-content p {
    font-size: 0.8rem;
  }

  .btn-dismiss svg {
    width: 18px;
    height: 18px;
  }
}
</style>
